import { z } from "zod";

export const errorSourceValues = ["error_boundary", "window_error", "unhandled_rejection"] as const;
export type ErrorSource = (typeof errorSourceValues)[number];

export const ERROR_SOURCE_LABELS: Record<ErrorSource, string> = {
  error_boundary: "React render",
  window_error: "Uncaught error",
  unhandled_rejection: "Unhandled promise",
};

export const errorStatusValues = ["open", "fixed"] as const;
export type ErrorStatus = (typeof errorStatusValues)[number];

/** Which list a row belongs to — `fixed` excludes anything seen since the fix. */
export const errorViewValues = ["open", "fixed"] as const;
export type ErrorView = (typeof errorViewValues)[number];

/**
 * One error report from the browser.
 *
 * Deliberately NOT accepting a fingerprint, occurrence count, timestamp, or
 * user id — all four are the server's to decide. Stacks are capped well below
 * anything a real one reaches; a caller sending more gets rejected rather than
 * silently truncated, so an over-long stack shows up as a bug here instead of
 * as mysteriously clipped data.
 */
export const errorReportSchema = z.object({
  message: z.string().min(1).max(1000),
  source: z.enum(errorSourceValues),
  stack: z.string().max(10000).nullable().optional(),
  componentStack: z.string().max(10000).nullable().optional(),
  routePath: z.string().max(500).nullable().optional(),
});

/**
 * Mark an error fixed (or reopen it).
 *
 * A fix note is REQUIRED when marking fixed — an entry in the fixed tab with no
 * explanation is nearly useless later. Reopening needs no note; the existing one
 * stays on the row as the record of what was tried.
 */
export const errorLogUpdateSchema = z
  .object({
    status: z.enum(errorStatusValues),
    fixNote: z.string().max(2000).nullable().optional(),
  })
  .refine((value) => value.status !== "fixed" || (value.fixNote ?? "").trim() !== "", {
    message: "Say what the fix was",
    path: ["fixNote"],
  });

export type ErrorLogUpdateInput = z.infer<typeof errorLogUpdateSchema>;

export type ErrorReportInput = z.infer<typeof errorReportSchema>;
